/**
 * The frame of every signed-in page (M31, ADR-0042): a side menu grouped by
 * role, collapsible to icons on large screens and a drawer on small ones;
 * a top bar with where you are, search (Ctrl+K) and your account; then the
 * page.
 */
import * as Menu from "@radix-ui/react-dropdown-menu";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronRight,
  ChevronsUpDown,
  ExternalLink,
  LogOut,
  Mail,
  Menu as MenuIcon,
  Monitor,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Phone,
  Search,
  Sun,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";

import { api, type SignedInUser, unwrap } from "@/api/client";
import { reachable, telHref, useHelpContact } from "@/api/help";
import { useChooseAuthority, useMyAuthorities } from "@/auth/authorities";
import { currentRole, useSetSession } from "@/auth/session";
import { IS_DESKTOP } from "@/desktop/mode";
import { cn } from "@/lib/utils";
import { type ThemeChoice, useTheme } from "@/theme-context";

import { CommandMenu } from "./command-menu";
import { activeItem, BRAND_ICON, type NavGroup, navGroups } from "./nav-items";
import { Button } from "./ui/button";
import { Dialog, SheetContent } from "./ui/dialog";
import { Tooltip } from "./ui/tooltip";

const MENU_CONTENT =
  "z-50 min-w-60 rounded-lg border border-border bg-popover p-1 text-foreground shadow-lg";
const MENU_ITEM =
  "flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2.5 text-sm outline-none " +
  "data-[highlighted]:bg-muted";
const COLLAPSED_KEY = "scrutiny.sidebar.collapsed";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    (parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts.at(-1)?.[0] ?? "") : "")
  ).toUpperCase();
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Which authority the work is for; a menu to switch when there are others. */
function AuthorityMenu({ session, collapsed }: { session: SignedInUser; collapsed: boolean }) {
  const { t } = useTranslation();
  const authorities = useMyAuthorities();
  const choose = useChooseAuthority();
  const queryClient = useQueryClient();
  const current = session.authority;
  const role = currentRole(session);
  const name = current?.name ?? t("roles.PLATFORM");
  const others = current ? (authorities.data ?? []).filter((authority) => authority.id !== current.id) : [];
  const label = current ? t("authority.workingFor", { name: current.name }) : name;
  const Brand = BRAND_ICON;
  const face = (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
        <Brand aria-hidden="true" className="size-4" />
      </span>
      <span className={cn("flex min-w-0 flex-1 flex-col text-left", collapsed && "sr-only")}>
        <span className="truncate text-sm font-semibold">{IS_DESKTOP ? t("desktop.title") : t("app.name")}</span>
        <span className="truncate text-xs text-muted-foreground">{name}{role && current ? ` · ${t(`roles.${role}`)}` : ""}</span>
      </span>
    </>
  );
  if (others.length === 0 || IS_DESKTOP) {
    return <div className="flex items-center gap-2.5 px-2 py-1.5" title={label}>{face}</div>;
  }
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <button type="button" aria-label={`${label}. ${t("authority.switch")}`} className="flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-sidebar-accent">
          {face}
          {collapsed ? null : <ChevronsUpDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />}
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content className={MENU_CONTENT} align="start" sideOffset={6}>
          <Menu.Label className="px-2.5 py-1.5 text-xs font-medium text-muted-foreground">{t("authority.switch")}</Menu.Label>
          {others.map((authority) => (
            <Menu.Item
              key={authority.id}
              className={MENU_ITEM}
              onSelect={() => {
                choose.mutate(authority.id, {
                  // Everything shown belongs to the authority in use.
                  onSuccess: () => { void queryClient.invalidateQueries(); },
                });
              }}
            >
              <span className="flex flex-col">
                <span>{authority.name}</span>
                <span className="text-xs text-muted-foreground">{t(`roles.${authority.role}`)}</span>
              </span>
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
} // prettier-ignore

function SideNav({
  groups,
  collapsed,
  onNavigate,
}: {
  groups: NavGroup[];
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("app.mainNavigation")} className="flex flex-1 flex-col gap-5 overflow-y-auto px-3 py-4">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <p className={cn("px-2 pb-1 text-xs font-medium text-muted-foreground", collapsed && "sr-only")}>{t(group.label)}</p>
          <ul className="flex flex-col gap-0.5">
            {group.items.map(({ to, label, icon: Icon, exact }) => {
              const link = (
                <NavLink
                  to={to}
                  end={exact}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    cn(
                      "flex min-h-9 items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-foreground",
                      collapsed && "justify-center px-0",
                      isActive && "bg-sidebar-accent text-foreground [&_svg]:text-primary",
                    )
                  }
                >
                  <Icon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
                  <span className={cn("truncate", collapsed && "sr-only")}>{t(label)}</span>
                </NavLink>
              );
              return <li key={to}>{collapsed ? <Tooltip content={t(label)} side="right">{link}</Tooltip> : link}</li>;
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
} // prettier-ignore

const THEME_ICONS: Record<ThemeChoice, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };

function AccountMenu({ session }: { session: SignedInUser }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useSetSession();
  const queryClient = useQueryClient();
  const { choice, setChoice } = useTheme();
  const role = currentRole(session);
  const help = useHelpContact().data;
  const signOut = useMutation({
    mutationFn: async () => {
      unwrap(await api.POST("/api/v1/auth/logout"));
    },
    onSettled: () => {
      queryClient.clear();
      setSession(null);
      void navigate("/sign-in", { replace: true });
    },
  });
  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button variant="ghost" className="gap-2 px-1.5" aria-label={t("app.account")}>
          <span aria-hidden="true" className="flex size-8 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">
            {initials(session.user.fullName)}
          </span>
          <span className="hidden max-w-40 truncate text-sm font-medium md:inline">{session.user.fullName}</span>
        </Button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content className={MENU_CONTENT} align="end" sideOffset={6}>
          <div className="flex flex-col px-2.5 py-2">
            <span className="text-sm font-semibold">{session.user.fullName}</span>
            <span className="truncate text-xs text-muted-foreground">{session.user.email}</span>
            {role ? <span className="mt-1 text-xs text-muted-foreground">{t(`roles.${role}`)}</span> : null}
          </div>
          <Menu.Separator className="my-1 h-px bg-border" />
          <Menu.Label className="px-2.5 py-1.5 text-xs font-medium text-muted-foreground">{t("app.theme.label")}</Menu.Label>
          <Menu.RadioGroup value={choice} onValueChange={(value) => { setChoice(value as ThemeChoice); }}>
            {(["light", "dark", "system"] as const).map((option) => {
              const Icon = THEME_ICONS[option];
              return (
                <Menu.RadioItem key={option} value={option} className={MENU_ITEM}>
                  <Icon aria-hidden="true" className="size-4 text-muted-foreground" />
                  {t(`app.theme.${option}`)}
                  <Menu.ItemIndicator className="ml-auto"><Check aria-hidden="true" className="size-4" /></Menu.ItemIndicator>
                </Menu.RadioItem>
              );
            })}
          </Menu.RadioGroup>
          {reachable(help) ? (
            <>
              <Menu.Separator className="my-1 h-px bg-border" />
              <Menu.Label className="px-2.5 py-1.5 text-xs font-medium text-muted-foreground">{t("help.menu")}</Menu.Label>
              {help.email ? (
                <Menu.Item asChild className={MENU_ITEM}>
                  <a href={`mailto:${help.email}`}><Mail aria-hidden="true" className="size-4 text-muted-foreground" />{t("help.email")}</a>
                </Menu.Item>
              ) : null}
              {help.phone ? (
                <Menu.Item asChild className={MENU_ITEM}>
                  <a href={telHref(help.phone)}><Phone aria-hidden="true" className="size-4 text-muted-foreground" />{t("help.call", { phone: help.phone })}</a>
                </Menu.Item>
              ) : null}
              {help.url ? (
                <Menu.Item asChild className={MENU_ITEM}>
                  <a href={help.url} target="_blank" rel="noreferrer">
                    <ExternalLink aria-hidden="true" className="size-4 text-muted-foreground" />{t("help.page")}{" "}
                    <span className="sr-only">{t("help.newTab")}</span>
                  </a>
                </Menu.Item>
              ) : null}
            </>
          ) : null}
          {IS_DESKTOP ? null : (
            <>
              <Menu.Separator className="my-1 h-px bg-border" />
              <Menu.Item className={MENU_ITEM} onSelect={() => { signOut.mutate(); }}>
                <LogOut aria-hidden="true" className="size-4 text-muted-foreground" />
                {t("app.signOut")}
              </Menu.Item>
            </>
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
} // prettier-ignore

/** Where you are: the menu group, then the page; the page links back. */
function Breadcrumbs({ groups }: { groups: NavGroup[] }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const found = activeItem(groups, pathname);
  if (!found) return null;
  const onItem = pathname === found.item.to;
  return (
    <nav aria-label={t("ui.breadcrumbs")} className="hidden min-w-0 items-center gap-1.5 text-sm sm:flex">
      <span className="text-muted-foreground">{t(found.group.label)}</span>
      <ChevronRight aria-hidden="true" className="size-3.5 text-muted-foreground" />
      {onItem ? (
        <span aria-current="page" className="truncate font-medium">{t(found.item.label)}</span>
      ) : (
        <Link to={found.item.to} className="truncate font-medium text-muted-foreground hover:text-foreground">{t(found.item.label)}</Link>
      )}
    </nav>
  );
} // prettier-ignore

export function AppShell({ session, children }: { session: SignedInUser; children?: ReactNode }) {
  const { t } = useTranslation();
  const groups = navGroups(session);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const toggleCollapsed = () => {
    setCollapsed((value) => {
      try { localStorage.setItem(COLLAPSED_KEY, value ? "0" : "1"); } catch { /* not stored */ }
      return !value;
    });
  };
  return (
    <>
          <div className="flex min-h-screen bg-canvas">
            <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:rounded-md focus:bg-card focus:p-3 focus:shadow-lg">
              {t("app.skipToContent")}
            </a>
            <aside className={cn("sticky top-0 hidden h-screen shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-200 lg:flex", collapsed ? "w-16" : "w-64")}>
              <div className="border-b border-sidebar-border p-3">
                <AuthorityMenu session={session} collapsed={collapsed} />
              </div>
              <SideNav groups={groups} collapsed={collapsed} />
              <div className="border-t border-sidebar-border p-3">
                <Button variant="ghost" size="sm" onClick={toggleCollapsed} className={cn("w-full justify-start text-muted-foreground", collapsed && "justify-center")} aria-label={t(collapsed ? "ui.expandMenu" : "ui.collapseMenu")}>
                  {collapsed ? <PanelLeftOpen aria-hidden="true" className="size-4" /> : <PanelLeftClose aria-hidden="true" className="size-4" />}
                  {collapsed ? null : t("ui.collapseMenu")}
                </Button>
              </div>
            </aside>
            <Dialog open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetContent title={t("app.mainNavigation")}>
                <div className="border-b border-sidebar-border p-3 pr-10">
                  <AuthorityMenu session={session} collapsed={false} />
                </div>
                <SideNav groups={groups} collapsed={false} onNavigate={() => { setMobileOpen(false); }} />
              </SheetContent>
            </Dialog>
            <div className="flex min-w-0 flex-1 flex-col">
              <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur lg:px-6">
                <Button variant="ghost" size="icon" className="lg:hidden" aria-label={t("ui.openMenu")} onClick={() => { setMobileOpen(true); }}>
                  <MenuIcon aria-hidden="true" className="size-5" />
                </Button>
                <Breadcrumbs groups={groups} />
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="secondary" className="h-9 w-9 justify-center px-0 text-muted-foreground sm:w-60 sm:justify-start sm:px-3" onClick={() => { setSearchOpen(true); }} aria-label={t("command.open")}>
                    <Search aria-hidden="true" className="size-4" />
                    <span className="hidden flex-1 text-left font-normal sm:inline">{t("command.search")}</span>
                    <kbd className="hidden rounded border border-border bg-muted px-1.5 font-sans text-[0.7rem] text-muted-foreground sm:inline">Ctrl K</kbd>
                  </Button>
                  <AccountMenu session={session} />
                </div>
              </header>
              {IS_DESKTOP ? (
                <p role="note" className="border-b border-border bg-warning-soft px-4 py-2 text-center text-sm font-medium">{t("desktop.banner")}</p>
              ) : null}
              <main id="main" tabIndex={-1} className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 outline-none lg:px-8 lg:py-8">
                {children ?? <Outlet />}
              </main>
            </div>
          </div>
          <CommandMenu session={session} open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
} // prettier-ignore
